import { supabase } from '../../lib/supabase';

export interface AuditLog {
  id: string;
  timestamp: string;
  action: string;
  user_name: string;
  details: string;
  source?: string; // 'custom' or 'system'
}

export interface EdgeLog {
  log_timestamp: string;
  event_message: string;
  metadata: any;
}

export async function getAuditLogs() {
  console.log('Fetching all audit logs with timestamp:', new Date().toISOString());
  
  try {
    // Create a fresh Supabase client for this request to avoid connection reuse
    const freshSupabase = supabase.from('audit_logs').select('*');
    
    // Add a timestamp parameter to prevent caching
    const timestamp = new Date().getTime();
    
    // First, check specifically for test user logs
    const testUserEmail = 'mattssubscriptions@gmail.com';
    const { data: testUserLogs, error: testUserError } = await freshSupabase
      .ilike('user_name', testUserEmail)
      .order('timestamp', { ascending: false })
      .then(response => {
        console.log(`Test user query executed at: ${timestamp}`);
        return response;
      });
      
    if (testUserError) {
      console.error('Error fetching test user logs:', testUserError);
    } else {
      console.log(`Found ${testUserLogs?.length || 0} logs for test user: ${testUserEmail}`);
    }
    
    // Then fetch all logs with a fresh connection
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .order('timestamp', { ascending: false })
      .then(response => {
        console.log(`Main query executed at: ${timestamp}`);
        return response;
      });

    if (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
    
    // Double-check if test user logs are in the results
    const testUserLogsInResults = data?.filter(log => 
      log.user_name && log.user_name.toLowerCase() === testUserEmail.toLowerCase()
    );
    
    console.log(`Test user logs in full results: ${testUserLogsInResults?.length || 0}`);
    console.log(`Total logs fetched: ${data?.length || 0} at ${timestamp}`);
    
    // Force a fresh result by cloning the data
    return JSON.parse(JSON.stringify(data || []));
  } catch (err) {
    console.error('Unexpected error in getAuditLogs:', err);
    throw err;
  }
}

export async function searchAuditLogs(searchTerm: string) {
  console.log(`Searching audit logs with term: "${searchTerm}" at ${new Date().toISOString()}`);
  
  try {
    // Add a timestamp parameter to prevent caching
    const timestamp = new Date().getTime();
    
    // First, check specifically for test user logs that match the search term
    const testUserEmail = 'mattssubscriptions@gmail.com';
    
    // Check if the search term might be related to the test user
    const isTestUserSearch = testUserEmail.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            searchTerm.toLowerCase().includes('test') ||
                            searchTerm.toLowerCase().includes('matt');
    
    if (isTestUserSearch) {
      console.log('Search term may be related to test user, performing specific test user search');
      const { data: testUserLogs, error: testUserError } = await supabase
        .from('audit_logs')
        .select('*')
        .ilike('user_name', testUserEmail)
        .order('timestamp', { ascending: false })
        .then(response => {
          console.log(`Test user search executed at: ${timestamp}`);
          return response;
        });
        
      if (testUserError) {
        console.error('Error fetching test user logs during search:', testUserError);
      } else {
        console.log(`Found ${testUserLogs?.length || 0} logs for test user during specific search`);
      }
    }
    
    // Then perform the regular search with a fresh connection
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .or(`action.ilike.%${searchTerm}%,user_name.ilike.%${searchTerm}%,details.ilike.%${searchTerm}%`)
      .order('timestamp', { ascending: false })
      .then(response => {
        console.log(`Search query executed at: ${timestamp}`);
        return response;
      });

    if (error) {
      console.error('Error searching audit logs:', error);
      throw error;
    }
    
    // Check if test user logs are in the search results
    const testUserLogsInResults = data?.filter(log => 
      log.user_name && log.user_name.toLowerCase() === testUserEmail.toLowerCase()
    );
    
    console.log(`Test user logs in search results: ${testUserLogsInResults?.length || 0}`);
    console.log(`Total search results: ${data?.length || 0} at ${timestamp}`);
    
    // Force a fresh result by cloning the data
    return JSON.parse(JSON.stringify(data || []));
  } catch (err) {
    console.error('Unexpected error in searchAuditLogs:', err);
    throw err;
  }
}

export async function getEdgeLogs() {
  const timestamp = new Date().getTime();
  console.log(`Fetching edge logs at: ${timestamp}`);
  
  try {
    // First try to use the RPC function if it exists
    try {
      const { data, error } = await supabase.rpc('get_edge_logs', {
        limit_count: 100, // Fetch the last 100 logs
        _timestamp: timestamp // Add timestamp to prevent caching
      });

      if (!error) {
        console.log(`Edge logs fetched via RPC: ${data?.length || 0} at ${timestamp}`);
        return data as EdgeLog[];
      }
      
      // If we get a "function does not exist" error, fall back to direct SQL
      if (error.message && error.message.includes('function does not exist')) {
        console.warn('get_edge_logs function not found, falling back to direct query');
      } else {
        console.error('Error calling get_edge_logs RPC:', error);
        throw error;
      }
    } catch (rpcErr) {
      console.warn('RPC method failed, falling back to direct query:', rpcErr);
    }
    
    // Fall back to direct SQL query if RPC fails
    // This is a simplified version that may work in some Supabase instances
    const { data, error } = await supabase.from('_analytics')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(100)
      .then(response => {
        console.log(`Analytics query executed at: ${timestamp}`);
        return response;
      });
      
    if (error) {
      // If direct query also fails, try another approach
      console.warn('Direct analytics query failed:', error);
      
      // As a last resort, try to query the edge_logs view if it exists
      const { data: edgeData, error: edgeError } = await supabase.from('edge_logs')
        .select('*')
        .order('log_timestamp', { ascending: false })
        .limit(100)
        .then(response => {
          console.log(`Edge logs view query executed at: ${timestamp}`);
          return response;
        });
        
      if (edgeError) {
        console.error('All edge log query methods failed:', edgeError);
        return []; // Return empty array if all methods fail
      }
      
      console.log(`Edge logs fetched via view: ${edgeData?.length || 0} at ${timestamp}`);
      return edgeData as EdgeLog[];
    }
    
    console.log(`Edge logs fetched via analytics: ${data?.length || 0} at ${timestamp}`);
    
    // Map the analytics data to EdgeLog format
    return data.map(row => ({
      log_timestamp: row.timestamp,
      event_message: row.event || row.event_message || 'Unknown event',
      metadata: row.metadata || {}
    })) as EdgeLog[];
  } catch (err) {
    console.error('Unexpected error in getEdgeLogs:', err);
    return []; // Return empty array on error to prevent breaking the UI
  }
}

export async function getAllLogs() {
  const timestamp = new Date().getTime();
  console.log(`Fetching all logs (combined) at: ${timestamp}`);
  
  try {
    // Get custom audit logs with a fresh request
    const customLogs = await getAuditLogs();
    console.log(`Custom logs fetched: ${customLogs?.length || 0} at ${timestamp}`);
    
    // Get system edge logs with a fresh request
    const edgeLogs = await getEdgeLogs();
    console.log(`Edge logs fetched: ${edgeLogs?.length || 0} at ${timestamp}`);
    
    // Convert edge logs to audit log format
    const convertedEdgeLogs = edgeLogs.map((log: EdgeLog) => {
      // Try to extract user information from metadata
      let userName = 'System';
      let action = 'API Request';
      let details = log.event_message;
      
      try {
        // Parse metadata if it's a string
        const metadata = typeof log.metadata === 'string' 
          ? JSON.parse(log.metadata) 
          : log.metadata;
          
        // Extract user info if available
        if (metadata && metadata.user) {
          userName = metadata.user.email || 'Unknown User';
        }
        
        // Extract action info if available
        if (metadata && metadata.method) {
          action = `${metadata.method} ${metadata.path || ''}`;
        }
        
        // Add more details
        details = JSON.stringify(metadata, null, 2);
      } catch (e) {
        console.error('Error parsing edge log metadata:', e);
      }
      
      return {
        id: `edge-${log.log_timestamp}-${timestamp}`, // Add timestamp to make IDs unique
        timestamp: log.log_timestamp,
        action,
        user_name: userName,
        details,
        source: 'system'
      } as AuditLog;
    });
    
    // Mark custom logs with source
    const markedCustomLogs = customLogs.map(log => ({
      ...log,
      source: 'custom'
    }));
    
    // Combine and sort all logs by timestamp (newest first)
    const allLogs = [...markedCustomLogs, ...convertedEdgeLogs].sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    
    console.log(`Total combined logs: ${allLogs.length} at ${timestamp}`);
    
    // Force a fresh result by cloning the data
    return JSON.parse(JSON.stringify(allLogs));
  } catch (err) {
    console.error('Error fetching all logs:', err);
    return [];
  }
}

export async function createAuditLog(log: Omit<AuditLog, 'id' | 'timestamp'>) {
  const timestamp = new Date().getTime();
  const logId = crypto.randomUUID(); // Generate a unique ID for this log
  
  try {
    console.log(`[AUDIT] Creating log for user: ${log.user_name}, action: ${log.action} at ${timestamp}`);
    
    // Create a complete log object with ID and timestamp
    const completeLog = {
      id: logId,
      timestamp: new Date().toISOString(),
      ...log
    };
    
    // First attempt: Use the standard Supabase client
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .insert([completeLog])
        .select()
        .single();

      if (error) {
        console.error(`[AUDIT] Primary insert failed for user ${log.user_name}:`, error);
        throw error;
      }

      console.log(`[AUDIT] Successfully created log via primary method: ${data?.id}`);
      return data;
    } catch (primaryError) {
      console.warn(`[AUDIT] Primary insert method failed, trying fallback:`, primaryError);
      
      // Second attempt: Use a direct SQL query approach with RPC
      try {
        console.log(`[AUDIT] Attempting RPC method for log ID: ${logId}`);
        
        // Properly await the RPC call and check for errors
        const { data: rpcData, error: rpcError } = await supabase.rpc('insert_audit_log', {
          log_id: logId,
          log_action: log.action,
          log_user_name: log.user_name,
          log_details: log.details,
          log_source: log.source || 'custom',
          log_timestamp: new Date().toISOString()
        });
        
        if (rpcError) {
          console.error(`[AUDIT] RPC method failed:`, rpcError);
          throw rpcError;
        }
        
        console.log(`[AUDIT] Successfully created log via RPC method for ID: ${logId}`);
        return completeLog;
      } catch (fallbackError) {
        console.error(`[AUDIT] Fallback method also failed:`, fallbackError);
        
        // Third attempt: Use a direct SQL insert with a raw query
        try {
          console.log(`[AUDIT] Attempting direct SQL insert for log ID: ${logId}`);
          
          // Create a SQL query string
          const sqlQuery = `
            INSERT INTO audit_logs (id, action, user_name, details, source, timestamp)
            VALUES ('${logId}', '${log.action.replace(/'/g, "''")}', '${log.user_name.replace(/'/g, "''")}', 
                    '${log.details.replace(/'/g, "''")}', '${log.source || 'custom'}', '${new Date().toISOString()}')
            RETURNING id;
          `;
          
          // Execute the raw query
          const { data: sqlData, error: sqlError } = await supabase.rpc('execute_sql', {
            sql_query: sqlQuery
          });
          
          if (sqlError) {
            console.error(`[AUDIT] Direct SQL insert failed:`, sqlError);
            throw sqlError;
          }
          
          console.log(`[AUDIT] Successfully created log via direct SQL for ID: ${logId}`);
          return completeLog;
        } catch (sqlError) {
          console.error(`[AUDIT] Direct SQL method also failed:`, sqlError);
          
          // Fourth attempt: Use a completely different approach - localStorage backup
          try {
            // Store in localStorage as a last resort
            const pendingLogs = JSON.parse(localStorage.getItem('pendingAuditLogs') || '[]');
            pendingLogs.push({
              ...completeLog,
              pendingTimestamp: new Date().toISOString()
            });
            localStorage.setItem('pendingAuditLogs', JSON.stringify(pendingLogs));
            console.log(`[AUDIT] Stored log in localStorage backup (${pendingLogs.length} pending)`);
            
            // Schedule a retry for localStorage items
            setTimeout(() => {
              processPendingLogs();
            }, 5000);
            
            return completeLog;
          } catch (localStorageError) {
            console.error(`[AUDIT] All methods failed, giving up:`, localStorageError);
            // At this point we've tried everything, just return the log object
            return completeLog;
          }
        }
      }
    }
  } catch (err) {
    console.error('[AUDIT] Critical error in createAuditLog:', err);
    console.error('[AUDIT] Log data attempted:', JSON.stringify(log));
    
    // Even in case of critical error, return something to prevent UI breakage
    return {
      id: logId,
      timestamp: new Date().toISOString(),
      ...log
    };
  }
}

// Process any pending logs stored in localStorage
export async function processPendingLogs() {
  try {
    const pendingLogs = JSON.parse(localStorage.getItem('pendingAuditLogs') || '[]');
    if (pendingLogs.length === 0) return;
    
    console.log(`[AUDIT] Processing ${pendingLogs.length} pending logs from localStorage`);
    
    // Process in batches of 5
    const batchSize = 5;
    const successfulIds = [];
    
    for (let i = 0; i < pendingLogs.length; i += batchSize) {
      const batch = pendingLogs.slice(i, i + batchSize);
      
      try {
        // Remove pendingTimestamp from each log
        const cleanBatch = batch.map((log: any) => {
          const { pendingTimestamp, ...cleanLog } = log;
          return cleanLog;
        });
        
        const { error } = await supabase
          .from('audit_logs')
          .insert(cleanBatch);
          
        if (!error) {
          // Mark these as successful
          successfulIds.push(...batch.map(log => log.id));
          console.log(`[AUDIT] Successfully processed batch of ${batch.length} pending logs`);
        } else {
          console.error(`[AUDIT] Error processing batch:`, error);
        }
      } catch (batchError) {
        console.error(`[AUDIT] Error processing batch:`, batchError);
      }
      
      // Small delay between batches
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // Remove successful logs from pending
    if (successfulIds.length > 0) {
      const remainingLogs = pendingLogs.filter((log: any) => !successfulIds.includes(log.id));
      localStorage.setItem('pendingAuditLogs', JSON.stringify(remainingLogs));
      console.log(`[AUDIT] Removed ${successfulIds.length} successful logs, ${remainingLogs.length} remaining`);
    }
  } catch (err) {
    console.error('[AUDIT] Error processing pending logs:', err);
  }
}

// Initialize: process any pending logs on startup
setTimeout(() => {
  processPendingLogs();
}, 5000);

// Direct database query function that bypasses all caching
export async function getAuditLogsDirectly() {
  const timestamp = new Date().getTime();
  console.log(`Direct database query for audit logs at: ${timestamp}`);
  
  try {
    // Use a raw SQL query to bypass any caching
    const { data, error } = await supabase.rpc('get_fresh_audit_logs', {
      timestamp_param: timestamp
    });
    
    if (error) {
      console.error('Error in direct audit logs query:', error);
      
      // Fall back to regular query if RPC fails
      console.log('Falling back to regular query with cache busting');
      
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('audit_logs')
        .select('*')
        .order('timestamp', { ascending: false })
        .then(response => {
          console.log(`Fallback query executed at: ${timestamp}`);
          return response;
        });
        
      if (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
        return [];
      }
      
      console.log(`Fallback query returned ${fallbackData?.length || 0} logs at ${timestamp}`);
      return fallbackData || [];
    }
    
    console.log(`Direct query returned ${data?.length || 0} logs at ${timestamp}`);
    return data || [];
  } catch (err) {
    console.error('Unexpected error in direct audit logs query:', err);
    return [];
  }
}
